import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, XCircle, Upload, Plus, Trash2, Globe, Settings, Check, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Domain limits per plan
const DOMAIN_LIMITS = {
  free: 1,
  pro: 9,
  lifetime: 9
};

const SetupPage = () => {
  const { token, user } = useAuth();
  const userPlan = user?.subscription?.plan || 'free';
  const maxDomains = DOMAIN_LIMITS[userPlan] || 1;
  
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [domains, setDomains] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [showWpHelp, setShowWpHelp] = useState(false);
  const [showGa4Help, setShowGa4Help] = useState(false);
  
  const [formData, setFormData] = useState({
    domain_name: '',
    wp_url: '',
    wp_username: '',
    wp_app_password: '',
    ga4_property_id: '',
    ga4_credentials: null,
  });

  useEffect(() => {
    if (token) {
      fetchDomains();
    }
  }, [token]);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API}/configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data.domains || []);
      // Show form if no domains configured
      if (!response.data.domains?.length) {
        setShowForm(true);
      }
    } catch (error) {
      setDomains([]);
      setShowForm(true);
    }
  };

  const resetForm = () => {
    setFormData({
      domain_name: '',
      wp_url: '',
      wp_username: '',
      wp_app_password: '',
      ga4_property_id: '',
      ga4_credentials: null,
    });
    setConnectionStatus(null);
    setEditingDomain(null);
  };

  const handleAddNew = () => {
    // Check domain limit
    if (domains.length >= maxDomains) {
      const planName = userPlan === 'lifetime' ? 'Lifetime (9 domains)' : userPlan === 'pro' ? 'Pro (1 domain)' : 'Free (1 domain)';
      toast.error(`Domain limit reached (${domains.length}/${maxDomains}). Upgrade your plan to add more domains.`);
      return;
    }
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (domain) => {
    setFormData({
      domain_name: domain.domain_name || '',
      wp_url: domain.wp_url,
      wp_username: domain.wp_username,
      wp_app_password: domain.wp_app_password,
      ga4_property_id: domain.ga4_property_id,
      ga4_credentials: domain.ga4_credentials,
    });
    setEditingDomain(domain);
    setShowForm(true);
    setConnectionStatus('configured');
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Delete ${domain.domain_name || domain.wp_url}?`)) return;
    
    try {
      await axios.delete(`${API}/config/${domain.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Domain deleted');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const handleActivate = async (domain) => {
    try {
      await axios.put(`${API}/config/${domain.id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${domain.domain_name || domain.wp_url} is now active`);
      fetchDomains();
    } catch (error) {
      toast.error('Failed to activate domain');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          setFormData({ ...formData, ga4_credentials: json });
          toast.success('GA4 credentials loaded');
        } catch (error) {
          toast.error('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  const testConnection = async () => {
    const url = formData.wp_url.trim();
    if (url.includes('/wp-admin') || url.includes('/blog') || url.endsWith('/')) {
      toast.error('Use base URL only (e.g., https://yoursite.com)');
      setConnectionStatus('failed');
      return;
    }

    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/wordpress/test`, {
        wp_url: url,
        wp_username: formData.wp_username,
        wp_app_password: formData.wp_app_password,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConnectionStatus('success');
        toast.success('WordPress connection successful!');
      } else {
        setConnectionStatus('failed');
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      setConnectionStatus('failed');
      toast.error('Connection failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ga4_credentials) {
      toast.error('Please upload GA4 credentials JSON file');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(editingDomain ? 'Domain updated!' : 'Domain added!');
      setShowForm(false);
      resetForm();
      fetchDomains();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Domain Setup</h1>
          <p className="text-slate-600 mt-1">Manage your WordPress sites and Google Analytics connections</p>
          <p className="text-sm text-slate-500 mt-1">
            Domains: {domains.length}/{maxDomains} 
            <span className="ml-2 text-xs px-2 py-0.5 bg-slate-100 rounded-full capitalize">{userPlan} plan</span>
          </p>
        </div>
        {domains.length > 0 && !showForm && (
          <Button 
            onClick={handleAddNew} 
            className="gap-2"
            disabled={domains.length >= maxDomains}
          >
            {domains.length >= maxDomains ? (
              <>
                <Lock className="w-4 h-4" />
                Limit Reached
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Domain
              </>
            )}
          </Button>
        )}
      </div>

      {/* Domain List */}
      {domains.length > 0 && !showForm && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-700">Your Domains</h2>
          {domains.map((domain) => (
            <Card key={domain.id} className={`${domain.is_active ? 'border-green-500 bg-green-50/50' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${domain.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Globe className={`w-5 h-5 ${domain.is_active ? 'text-green-600' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{domain.domain_name || domain.wp_url}</span>
                        {domain.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{domain.wp_url}</p>
                      <p className="text-xs text-slate-400">GA4: {domain.ga4_property_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!domain.is_active && (
                      <Button variant="outline" size="sm" onClick={() => handleActivate(domain)} className="gap-1">
                        <Check className="w-3 h-3" />
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(domain)}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(domain)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="space-y-6">
          {domains.length > 0 && (
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingDomain ? 'Edit Domain' : 'Add New Domain'}</h2>
              <Button variant="ghost" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
            </div>
          )}

          {connectionStatus === 'failed' && (
            <Card className="border-warning">
              <CardHeader className="py-3 bg-warning/5">
                <CardTitle className="text-sm font-medium text-warning flex items-center">
                  <XCircle className="w-4 h-4 mr-2" />
                  Connection Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3">
                <ul className="space-y-1 text-xs text-slate-600">
                  <li>• Use base URL only (e.g., https://yoursite.com)</li>
                  <li>• Generate Application Password in WordPress Users → Profile</li>
                  <li>• Ensure REST API is not blocked by security plugins</li>
                </ul>
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">WordPress Configuration</CardTitle>
                    <CardDescription>Enter your WordPress site credentials</CardDescription>
                  </div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-blue-600 p-0 h-auto"
                    onClick={() => setShowWpHelp(!showWpHelp)}
                  >
                    {showWpHelp ? 'Hide Guide' : 'Setup Guide'}
                  </Button>
                </div>
                {showWpHelp && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm space-y-3">
                    <h4 className="font-semibold text-blue-900">How to get WordPress Application Password:</h4>
                    <ol className="list-decimal list-inside space-y-2 text-blue-800">
                      <li>Log in to your WordPress admin dashboard</li>
                      <li>Go to <strong>Users → Profile</strong> (or click your username)</li>
                      <li>Scroll down to <strong>"Application Passwords"</strong> section</li>
                      <li>Enter a name (e.g., "WP Magica")</li>
                      <li>Click <strong>"Add New Application Password"</strong></li>
                      <li>Copy the generated password (format: xxxx xxxx xxxx xxxx)</li>
                    </ol>
                    <div className="pt-2 border-t border-blue-200">
                      <p className="text-blue-700"><strong>Note:</strong> Application Passwords require WordPress 5.6+ and HTTPS.</p>
                      <p className="text-blue-700 mt-1">If you don't see this option, check if it's disabled by a security plugin.</p>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="domain_name">Domain Name (optional)</Label>
                    <Input
                      id="domain_name"
                      placeholder="My Blog"
                      value={formData.domain_name}
                      onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="wp_url">Site URL *</Label>
                    <Input
                      id="wp_url"
                      type="url"
                      placeholder="https://yoursite.com"
                      value={formData.wp_url}
                      onChange={(e) => setFormData({ ...formData, wp_url: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wp_username">Username *</Label>
                    <Input
                      id="wp_username"
                      placeholder="admin"
                      value={formData.wp_username}
                      onChange={(e) => setFormData({ ...formData, wp_username: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="wp_app_password">App Password *</Label>
                    <Input
                      id="wp_app_password"
                      type="password"
                      placeholder="xxxx xxxx xxxx"
                      value={formData.wp_app_password}
                      onChange={(e) => setFormData({ ...formData, wp_app_password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <Button
                  type="button"
                  onClick={testConnection}
                  disabled={testingConnection || !formData.wp_url || !formData.wp_username || !formData.wp_app_password}
                  variant="outline"
                  className="w-full"
                >
                  {testingConnection ? 'Testing...' : 'Test Connection'}
                </Button>
                
                {connectionStatus === 'success' && (
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Connection successful
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">Google Analytics 4</CardTitle>
                    <CardDescription>Upload your GA4 service account credentials</CardDescription>
                  </div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-blue-600 p-0 h-auto"
                    onClick={() => setShowGa4Help(!showGa4Help)}
                  >
                    {showGa4Help ? 'Hide Guide' : 'Setup Guide'}
                  </Button>
                </div>
                {showGa4Help && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm space-y-4">
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Step 1: Get your GA4 Property ID</h4>
                      <ol className="list-decimal list-inside space-y-1 text-blue-800">
                        <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Analytics</a></li>
                        <li>Click <strong>Admin</strong> (gear icon) → Select your property</li>
                        <li>Go to <strong>Property Settings</strong></li>
                        <li>Copy the <strong>Property ID</strong> (numeric, e.g., 123456789)</li>
                      </ol>
                    </div>
                    <div className="pt-3 border-t border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Step 2: Create Service Account</h4>
                      <ol className="list-decimal list-inside space-y-1 text-blue-800">
                        <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                        <li>Create a new project or select existing one</li>
                        <li>Go to <strong>APIs & Services → Library</strong></li>
                        <li>Search and enable <strong>"Google Analytics Data API"</strong></li>
                        <li>Go to <strong>APIs & Services → Credentials</strong></li>
                        <li>Click <strong>Create Credentials → Service Account</strong></li>
                        <li>Name it and click <strong>Create</strong> (skip optional steps)</li>
                        <li>Click on the service account → <strong>Keys</strong> tab</li>
                        <li>Click <strong>Add Key → Create new key → JSON</strong></li>
                        <li>Download and save the JSON file</li>
                      </ol>
                    </div>
                    <div className="pt-3 border-t border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Step 3: Grant Access in GA4</h4>
                      <ol className="list-decimal list-inside space-y-1 text-blue-800">
                        <li>Copy the service account email from the JSON file</li>
                        <li>Go to GA4 → <strong>Admin → Property Access Management</strong></li>
                        <li>Click <strong>+ Add users</strong></li>
                        <li>Paste the service account email</li>
                        <li>Set role to <strong>Viewer</strong> and save</li>
                      </ol>
                    </div>
                    <div className="pt-3 border-t border-blue-200 flex gap-3">
                      <a 
                        href="https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs"
                      >
                        Enable Analytics API →
                      </a>
                      <a 
                        href="https://console.cloud.google.com/iam-admin/serviceaccounts" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs"
                      >
                        Create Service Account →
                      </a>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="ga4_property_id">GA4 Property ID *</Label>
                  <Input
                    id="ga4_property_id"
                    placeholder="123456789"
                    value={formData.ga4_property_id}
                    onChange={(e) => setFormData({ ...formData, ga4_property_id: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Service Account Credentials *</Label>
                  <div className="mt-2">
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-200 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50">
                      <Upload className="w-4 h-4 mr-2" />
                      {formData.ga4_credentials ? 'Credentials Loaded ✓' : 'Upload JSON'}
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Saving...' : editingDomain ? 'Update Domain' : 'Add Domain'}
            </Button>
          </form>
        </div>
      )}

      {/* Empty State */}
      {domains.length === 0 && !showForm && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Globe className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No domains configured</h3>
            <p className="text-slate-500 mb-4">Add your first WordPress site to get started</p>
            <Button onClick={handleAddNew}>
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SetupPage;
